<template><!-- STEP 3 -->
  <main id="mainContent" class="user-info">
    <Stepper :step="stepNum"/>
    <Timer ref="timer" />
    <div class="app-main-content">
      <h1>{{ screenData.title }}</h1>
      <p>{{ screenData.description }}</p>

      <div class="app-content-panels">
        <!-- form -->
        <form class="app-panel-left">
          <div>
            <button v-if="(bothAllowed || onlyIndividual) && !isOcaBusiness"
                    type="button"
                    :class="['app-btn-selectable', { '__active': isIndividual }]"
                    ref="btnIndividual"
                    @click.prevent="isIndividual = true">
              I'm an individual
            </button>
            <button v-if="(bothAllowed || onlyBusiness) && !isOcaIndividual"
                    type="button"
                    :class="['app-btn-selectable', { '__active': !isIndividual }]"
                    ref="btnBusiness"
                    @click.prevent="isIndividual = false">
              I'm a business
            </button>
          </div>

          <p class="app-required-indicator __before">Required field</p>

          <template v-if="isIndividual">
            <UserIndividual :verify="verifyIndividual"
                            @doResetVerify="verifyIndividual = false"
                            @doScroll="scrollToFirstError"
                            @doValidated="routeToNext"
                            ref="individual" />
          </template>
          <template v-if="!isIndividual">
            <UserBusiness :verify="verifyBusiness"
                          @doResetVerify="verifyBusiness = false"
                          @doScroll="scrollToFirstError"
                          @doValidated="routeToNext"
                          ref="business" />
          </template>

          <p class="app-invalid-msg" hidden ref="lostHold">
            The selected reservation is no longer available. Return to previous screen
            to select another date and time.
          </p>

          <BaseButtonGroup>
            <button type="button" @click="handlePseudoRouting(true)">{{ language.btnPrev }}</button>
            <button type="button" class="app-btn-primary" @click="handlePseudoRouting(false)">{{ language.btnNext }}</button>
          </BaseButtonGroup>
        </form>

        <!-- right panel -->
        <AsideForUser />
      </div>
    </div>
  </main>
</template>


<script setup>
import { ref, computed, onMounted, nextTick, onBeforeMount  } from 'vue';
import { useStore } from 'vuex';
import { useRouter } from 'vue-router';
import appConfig from '@/app.config'
import Stepper from '@/components/reservation/Stepper.vue';
import Timer from '@/components/reservation/Timer.vue';
import UserIndividual from '@/components/reservation/user/UserIndividual.vue';
import UserBusiness from '@/components/reservation/user/UserBusiness.vue';
import AsideForUser from '@/components/reservation/aside/AsideForUser.vue';
import BaseButtonGroup from '@/components/ui/BaseButtonGroup.vue';
import { useHead } from '@unhead/vue';
import { usePhoneFormatter } from '@/composables/usePhoneFormatter';
import { RESERVATION_FIND } from '@/store/_commons';

// Use usehead to manage the page meta
useHead({
  title: `Your Information | ${appConfig.shortName}`,
  meta: [
    {
      name: 'description',
      content: 'Select a date and time for your service. Reservations are added throughout the week.'
    }
  ]
})

// Vuex store and router
const store = useStore();
const router = useRouter();

// Reactive state ref
const stepNum = ref(3);
const isIndividual = ref(true);
const verifyIndividual = ref(false);
const verifyBusiness = ref(false);

// Template Refs
const timer = ref(null);
const individual = ref(null);
const business = ref(null);
const lostHold = ref(null);

// Computed properties
const screenData = computed(() => store.state.user.STEPS[stepNum.value]);
const language = computed(() => store.state.user.LANG);
const allowedCustomer = computed(() => store.state.user.resTypeForCustomer.allowedCustomerTypeId);

const { formatPhoneForRims } = usePhoneFormatter();

const onlyIndividual = computed(() => {
  const type = store.state.user.ALL_CUSTOMER_TYPES.find(item => item.type === 'INDIVIDUAL');
  return allowedCustomer.value === (type ? type.id : null);
});

const onlyBusiness = computed(() => {
  const type = store.state.user.ALL_CUSTOMER_TYPES.find(item => item.type === 'BUSINESS');
  return allowedCustomer.value === (type ? type.id : null);
});

const bothAllowed = computed(() => {
  const type = store.state.user.ALL_CUSTOMER_TYPES.find(item => item.type === 'BOTH');
  return allowedCustomer.value === (type ? type.id : null);
});

const resTypeForCustomer = computed(() => store.state.user.resTypeForCustomer);
const reservationSlotId = computed(() => store.state.user.reservationSlotId);
const resDetails = computed(() => store.state.user.resDetails);
const customerDetail = computed(() => store.state.user.customerDetail);
const PAGES = computed(() => store.state.user.PAGES);
const modifyReservation = computed(() => store.state.user.modifyReservation);

const onlineToken = computed(() => store.state.user.onlineToken); 
const isOcaIndividual = computed(() =>
  !!onlineToken.value && !!customerDetail.value && customerDetail.value.dateOfBirth !== '0001-01-01T00:00:00'
);
const isOcaBusiness = computed(() =>
  !!onlineToken.value && !!customerDetail.value && customerDetail.value.dateOfBirth === '0001-01-01T00:00:00'
);

const FEE_VISIT_TYPES = new Set([31, 32, 33]);

const count = ref(1);

onBeforeMount(() => {
  if (store.state.user.reservationSlotId <= 0) {
    router.push({ name: PAGES.value.selectSlot });
  }
});

// Lifecycle hooks
onMounted(() => {
  if(isOcaBusiness.value) {
    isIndividual.value = false;
  }
  else {
    isIndividual.value = resDetails.value.userInfo ? !resDetails.value.userInfo.isBusinessCustomer : true;
  }
});

// Methods
const getFormattedDob = (dob) => {
  // This function is already plain JS, no changes needed
  if ((dob.match(new RegExp(/^\d{2}\/\d{2}\/\d{4}$/, 'g')) || []).length === 1) {
    return `${dob.substr(6, 4)}-${dob.substr(0, 2)}-${dob.substr(3, 2)}`;
  }
  return dob;
};

const handlePseudoRouting = async (isPrev = false) => {
  if (isPrev) {
    router.push({ name: PAGES.value.selectSlot });
    return;
  }

  // Have child component initiate form fields validation
  if (isIndividual.value) {
    verifyIndividual.value = false;
    await individual.value.validateIndividual();
    verifyIndividual.value = true;
  } else {
    verifyBusiness.value = false;
    await business.value.validateBusiness();
    verifyBusiness.value = true;
  }

  // routeToNext() will be called from the child component's `@doValidated` event
};

const routeToNext = async (data) => {
  // TASK 943720 ~ ensure dob format is good for system
  if (isIndividual.value && data.dateOfBirth) {
    data.dateOfBirth = getFormattedDob(data.dateOfBirth);
  }

  // Set visible form data in session for use in the store
  const modified = { ...resDetails.value.userInfo, ...data };

  await store.dispatch('user/setReservationDetails', [{ property: 'userInfo', value: modified }]);

  // Now resDetails.value will have the updated userInfo

  if (isIndividual.value) {
    verifyIndividual.value = false;
  } else {
    verifyBusiness.value = false;
  }

  // User inputs validated, proceed with routing
  await routerPush();
};

const routerPush = async () => {
  const nextRoute = PAGES.value.confirmRes;
  const hasLicense = !!resDetails.value.userInfo.licenseNumber;
  const isModify = resDetails.value.userInfo.reservationSendConfirmationRequest.isModifyRequest;
  const isVisit = resTypeForCustomer.value?.visitCategoryId === 7;
  const hasSlotAndCustomer = reservationSlotId.value > 0 && customerDetail.value;
  const useMfa = window.config.USEMFA;

  const eligibilityCheck = (hasLicense && !(isModify && isVisit && resTypeForCustomer.value)) || (isVisit && hasSlotAndCustomer);

  const otpPayload = () => ({
    verificationCode: resDetails.value.userInfo.reservationSendConfirmationRequest.oneTimeOtp,
    attemptCount: count.value++,
  });
  
  // Only check for existing reservations on a new booking (not during modify flow)
  const navigateToNextRoute = async () => {
    // Skip eligibility check if onlineToken is present (user already verified from OCA)
    if(onlineToken.value) {
      router.push({ name: nextRoute });
      return;
    }

    const isActualModifyFlow = (store.state.user.resSelectedForUpdate?.visitTypeId ?? 0) > 0;
    if (!isActualModifyFlow) {
      const contactInfo = resDetails.value.userInfo.reservationSendConfirmationRequest;
      const verifiedBy = contactInfo.verifiedContactType; // 'email' or 'phone'

      const searchEmail = verifiedBy === 'email' ? (contactInfo.email || '') : '';
      const searchPhone = verifiedBy === 'phone' ? (contactInfo.phone || '') : '';

      // Check for existing reservations when email was verified or phone was verified (not both empty)
      if (searchEmail || searchPhone) {
        const info = {
          appointmentNumber: '',
          emailAddress: searchEmail,
          phoneNumber: searchPhone ? formatPhoneForRims(searchPhone) : ''
        };
        const allFound = await store.dispatch(RESERVATION_FIND, info);
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        const futureScheduled = allFound.filter(r =>
          r.appointmentStatusDescription?.toUpperCase() === 'SCHEDULED' &&
          new Date(r.scheduleDate) >= today
        );

        // Any fee-type involved (new attempt or any existing match) bypasses this page entirely
        const newIsFee = FEE_VISIT_TYPES.has(store.state.user.resType);
        const anyExistingIsFee = futureScheduled.some(r => FEE_VISIT_TYPES.has(r.visitTypeId));

        if (futureScheduled.length > 0 && !newIsFee && !anyExistingIsFee) {
          store.commit('user/setState',  {
            property: 'newResSnapshot',
            value: {
              resType: store.state.user.resType,
              resDetails: JSON.parse(JSON.stringify(store.state.user.resDetails)),
              resFeeApplicable: store.state.user.resFeeApplicable,
              resTypeForCustomer: JSON.parse(JSON.stringify(store.state.user.resTypeForCustomer)),
            }
          });
          store.commit('user/setReservationsFound', futureScheduled);
          router.push({ name: PAGES.value.updateRes, query: { from: 'newres' } });
          return;
        }
      }
    }
    router.push({ name: nextRoute });
  };

  const verifyMfa = async () => {
    const result = await store.dispatch('reservation/setVerifyMfaRequest', otpPayload());
    if (result?.isSucess || result ) {
      await navigateToNextRoute();
    } else {
      console.error('MFA verification failed');
    }
  };

  const mfaNotRequired = async () => {
    if (!useMfa || hasSlotAndCustomer) {
      await navigateToNextRoute();
      return true;
    }
    return false;
  };

  const handleMfa = async () => {
    if (await mfaNotRequired()) return;
    await verifyMfa();
  };

  // Eligibility check 
  if (eligibilityCheck) {
    if (!modifyReservation.value) {
      try {
        const eligible = await store.dispatch('reservation/checkCustomerEligibility', nextRoute);
        if (!eligible) return;
        await handleMfa();
        return;
      } catch (error) {
        console.log(error);
        return;
      }
    }

    await handleMfa();
    return;
    // no license provided means no eligibility check - also applies to Business
  }

  // No eligibility check
  try {
    await handleMfa();
  } catch (error) {
    console.log(error);
  }
  // no license provided means no eligibility check - also applies to Business
};

const scrollToFirstError = async () => {
  await nextTick();
  const nodeList = document.querySelectorAll('.app-invalid-group');
  // scroll to first error element, if any (useful for small screens)
  if (nodeList && nodeList.length > 0) {
    // Await nextTick to ensure the DOM is updated before scrolling
    nodeList[0].scrollIntoView({ behavior: 'smooth' });
  } else {
    console.log("scrollToFirstError");
  }
};
</script>


<style scoped lang="scss">
.user-info {
  .app-btn-selectable {
    margin-right: 1.25em;
    padding: 0.75em 0.5em;
    width: 200px;
  }
  form {
    p {
      padding: 1.125em 0;
    }
  }
  .app-panel-left{
    max-width: 800px;
  }
}

.app-button-group {
  button {
    margin-bottom: 1.125em;
    &:not(:last-child) {
      margin-right: 1em;
    }
    &.disabled {
      cursor: not-allowed;
      opacity: 0.7;
    }
  }
}
</style>
